import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, Film, Layers, Sparkles } from "lucide-react";
import { SEED } from "@/constants/testIds";
import { toast } from "sonner";

function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`font-display text-3xl mt-2 tabular-nums ${accent ? "text-brand" : "text-gray-900"}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

const statusPill = {
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rendering: "bg-amber-50 text-amber-700 ring-amber-200",
  queued:    "bg-gray-100 text-gray-600 ring-gray-200",
  failed:    "bg-red-50 text-red-700 ring-red-200",
};

export default function Dashboard() {
  const [perf, setPerf] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [renders, setRenders] = useState([]);

  const load = async () => {
    try {
      const [p, c, r] = await Promise.all([
        api.get("/performance/summary"),
        api.get("/campaigns"),
        api.get("/renders"),
      ]);
      setPerf(p.data); setCampaigns(c.data); setRenders(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const t = perf?.totals || {};

  const seed = async () => {
    try {
      await api.post("/seed");
      toast.success("Demo data seeded");
      load();
    } catch { toast.error("Seed failed"); }
  };

  return (
    <div className="p-8 space-y-8 max-w-[1500px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Daily ops across campaigns, renders, and performance.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="live-dot" />
          <span className="text-xs text-gray-500">Live · syncing</span>
          {campaigns.length === 0 && (
            <button
              data-testid={SEED.seedBtn} onClick={seed}
              className="ml-4 inline-flex items-center gap-2 rounded-lg border border-brand text-brand hover:bg-brand hover:text-white px-3.5 py-2 text-xs font-medium transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5" /> Seed Codira examples
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Impressions" value={(t.impressions || 0).toLocaleString()} sub={`${perf?.daily?.length || 0}-day window`} />
        <Stat label="CTR" value={`${((t.ctr || 0) * 100).toFixed(2)}%`} sub={`${(t.clicks || 0).toLocaleString()} clicks`} />
        <Stat label="Spend" value={`$${(t.spend || 0).toLocaleString()}`} sub={`$${t.cpa || 0} / CPA`} />
        <Stat accent label="Active Campaigns" value={t.active_campaigns || 0} sub={`${t.total_renders || 0} renders shipped`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Layers className="h-4 w-4 text-brand" /> Campaigns
            </h3>
            <Link to="/campaigns" className="text-xs text-brand hover:underline flex items-center gap-1">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {campaigns.length === 0 && (
              <div className="px-5 py-10 text-center text-gray-400 text-sm">
                No campaigns yet — seed examples or create one.
              </div>
            )}
            {campaigns.slice(0, 5).map((c) => (
              <Link key={c.id} to={`/campaigns/${c.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className="w-1.5 h-10 rounded-full shrink-0" style={{ background: c.brand_color || "#ee9037" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {c.objective} · {c.status} · {c.variation_count || 0} variations · {c.render_count || 0} renders
                  </p>
                </div>
                <ArrowUpRight className="h-3.5 w-3.5 text-gray-400" />
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Film className="h-4 w-4 text-brand" /> Recent renders
            </h3>
            <Link to="/renders" className="text-xs text-brand hover:underline flex items-center gap-1">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {renders.length === 0 && (
              <div className="px-5 py-10 text-center text-gray-400 text-sm">No renders yet.</div>
            )}
            {renders.slice(0, 6).map((r) => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md ring-1 text-[10px] font-medium uppercase tracking-wider ${statusPill[r.status] || "bg-gray-100 text-gray-600 ring-gray-200"}`}>
                  {r.status}
                </span>
                <span className="text-xs text-gray-500 tabular-nums">{r.progress || 0}%</span>
                <span className="text-xs text-gray-400 font-mono ml-auto">{r.id.slice(0, 8)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
